import type { ClassifyNeighbor } from "../api/client";
import styles from "./ClassifyDetailPanel.module.css";

/** Steps where a doc has moved past classify and can quietly receive attachments.
 *  Keep in sync with ATTACH_TARGET_STEPS in apps/api/src/routes/documents/attach-service.ts.
 *  NOTE: doc-step vocabulary uses "qc" (not "qa") — "qa" is a role-permission stage, different vocab. */
const ADVANCED_STEPS = new Set(["key", "verify", "qc", "supervisor"]);

/**
 * R-262 5d-5b — scan-order neighbor cards (prev / next, any step).
 *
 * Advanced neighbors (key/verify/qc/supervisor) AND held-clean neighbors
 * (ready@classify — R-265 5d-5c) offer quiet-attach actions so an operator
 * can send stray pages to the doc scanned just before or after, without
 * disturbing that doc's step, status, lock, or assignment. Buttons are gated
 * on the neighbor's `allowsAttachments` (mirrors the attach-service 409).
 *
 * Parked neighbors are contextual display only — the move/merge sibling
 * selects already handle the parked-to-parked case.
 *
 * Parent (ClassifyDetailPanel) owns wiring: `onAttachPage` / `onAttachAll`
 * receive the target neighbor id and delegate to `useParkedDocAction`.
 */
export function NeighborStrip({
  prev,
  next,
  busy,
  onAttachPage,
  onAttachAll,
}: {
  prev: ClassifyNeighbor | null;
  next: ClassifyNeighbor | null;
  busy: boolean;
  onAttachPage: (targetId: string) => void;
  onAttachAll: (targetId: string) => void;
}) {
  function stepLabel(n: ClassifyNeighbor): string {
    if (n.currentStep === "classify") {
      return n.status === "ready" ? "waiting" : "needs fix";
    }
    return `at ${n.currentStep}`;
  }

  function renderCard(n: ClassifyNeighbor, label: string) {
    // R-265 5d-5c — attach targets: advanced steps OR a held-clean batch-mate
    // (ready@classify); the form must accept attachments.
    const isHeldClean = n.currentStep === "classify" && n.status === "ready";
    const canAttach = (ADVANCED_STEPS.has(n.currentStep) || isHeldClean) && n.allowsAttachments;
    return (
      <div className={styles.neighborCard} key={n.id}>
        <span className={styles.neighborLabel}>{label}</span>
        <span>{n.displayId ?? n.filename}</span>
        <span>{n.formName ?? "unidentified"}</span>
        <span className={styles.neighborStep}>{stepLabel(n)}</span>
        {canAttach && (
          <>
            <button
              type="button"
              className={styles.btn}
              disabled={busy}
              onClick={() => onAttachPage(n.id)}
            >
              Send selected page here
            </button>
            <button
              type="button"
              className={styles.btn}
              disabled={busy}
              onClick={() => onAttachAll(n.id)}
            >
              Send all pages here
            </button>
          </>
        )}
      </div>
    );
  }

  if (!prev && !next) return null;

  return (
    <div className={styles.neighborStrip} aria-label="Batch neighbors">
      {prev && renderCard(prev, "Prev")}
      {next && renderCard(next, "Next")}
    </div>
  );
}
